import { createElement } from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';

import { RequirePermission } from '@/components/RequirePermission';
import { getAdminLandingPath } from '@/layouts/mobile-access';
import { MobileUnavailable } from '@/layouts/MobileAdminLayout';
import { useIsMobile } from '@/hooks/useIsMobile';
import { ADMIN_ROUTE_PERMISSIONS } from '@/layouts/menu-access';
import { useAuthStore } from '@/stores/auth';
import { PublicOnly, RequireAuth, RouteLoading } from './router/RouteGuards';

function NavigateToFirstAllowed() {
  const mobile = useIsMobile();
  const permissions = useAuthStore((s) => s.permissions);
  return createElement(Navigate, { to: getAdminLandingPath(permissions, mobile), replace: true });
}

export const router = createBrowserRouter([
  {
    HydrateFallback: RouteLoading,
    element: createElement(PublicOnly),
    children: [{ path: '/login', lazy: () => import('@/pages/login') }],
  },
  {
    HydrateFallback: RouteLoading,
    element: createElement(RequireAuth),
    children: [
      {
        lazy: () => import('@/layouts/AdminLayout'),
        children: [
          { path: 'mobile-unavailable', element: createElement(MobileUnavailable) },
          { path: '*', element: createElement(NavigateToFirstAllowed) },
          // ── 数据中心 ──
          {
            element: createElement(RequirePermission, {
              permission: ADMIN_ROUTE_PERMISSIONS.dashboard,
            }),
            children: [{ index: true, lazy: () => import('@/pages/dashboard') }],
          },
          {
            element: createElement(RequirePermission, {
              permission: ADMIN_ROUTE_PERMISSIONS.dashboardTrends,
            }),
            children: [
              { path: 'dashboard/trends', lazy: () => import('@/pages/dashboard/trends') },
            ],
          },
          {
            element: createElement(RequirePermission, {
              permission: ADMIN_ROUTE_PERMISSIONS.dashboardAnalytics,
            }),
            children: [
              { path: 'operation-analytics', lazy: () => import('@/pages/operation-analytics') },
            ],
          },
          {
            element: createElement(RequirePermission, {
              permission: ADMIN_ROUTE_PERMISSIONS.dashboardModelUsage,
            }),
            children: [
              {
                path: 'dashboard/model-usage',
                lazy: () => import('@/pages/dashboard/model-usage'),
              },
            ],
          },

          // ── 用户中心 ──
          {
            element: createElement(RequirePermission, {
              permission: ADMIN_ROUTE_PERMISSIONS.users,
            }),
            children: [{ path: 'users', lazy: () => import('@/pages/users') }],
          },
          {
            element: createElement(RequirePermission, {
              permission: ADMIN_ROUTE_PERMISSIONS.userDetail,
            }),
            children: [{ path: 'users/:userId', lazy: () => import('@/pages/users/detail') }],
          },
          {
            element: createElement(RequirePermission, { permission: 'invite_codes:list' }),
            children: [{ path: 'invite-codes', lazy: () => import('@/pages/invite-codes') }],
          },
          {
            element: createElement(RequirePermission, { permission: 'account_appeals:list' }),
            children: [{ path: 'account-appeals', lazy: () => import('@/pages/account-appeals') }],
          },

          // ── 企业认证 ──
          {
            element: createElement(RequirePermission, { permission: 'certifications:list' }),
            children: [{ path: 'certifications', lazy: () => import('@/pages/certifications') }],
          },
          {
            element: createElement(RequirePermission, { permission: 'certifications:detail' }),
            children: [
              {
                path: 'certifications/:certId',
                lazy: () => import('@/pages/certifications/detail'),
              },
            ],
          },

          // ── 组织管理 ──
          {
            element: createElement(RequirePermission, {
              permission: ADMIN_ROUTE_PERMISSIONS.organizations,
            }),
            children: [{ path: 'organizations', lazy: () => import('@/pages/organizations') }],
          },
          {
            element: createElement(RequirePermission, {
              permission: 'org_dissolution_requests:list',
            }),
            children: [
              {
                path: 'org-dissolution-requests',
                lazy: () => import('@/pages/org-dissolution-requests'),
              },
            ],
          },
          {
            element: createElement(RequirePermission, {
              permission: ADMIN_ROUTE_PERMISSIONS.organizationDetail,
            }),
            children: [
              { path: 'organizations/:orgId', lazy: () => import('@/pages/organizations/detail') },
            ],
          },

          // ── 内容管理 ──
          {
            element: createElement(RequirePermission, { permission: 'content:projects:list' }),
            children: [
              { path: 'projects', lazy: () => import('@/pages/projects') },
              { path: 'projects/:projectId', lazy: () => import('@/pages/projects/detail') },
            ],
          },
          {
            element: createElement(RequirePermission, { permission: 'project_exports:list' }),
            children: [{ path: 'project-exports', lazy: () => import('@/pages/project-exports') }],
          },
          {
            element: createElement(RequirePermission, { permission: 'content:screenplays:list' }),
            children: [{ path: 'screenplays', lazy: () => import('@/pages/placeholder') }],
          },
          {
            element: createElement(RequirePermission, { permission: 'knowledge:list' }),
            children: [
              { path: 'knowledge', lazy: () => import('@/pages/knowledge') },
              {
                path: 'knowledge/:projectId',
                lazy: () => import('@/pages/knowledge/parent-detail'),
              },
              {
                path: 'knowledge/:projectId/episodes/:scopeProjectId',
                lazy: () => import('@/pages/knowledge/detail'),
              },
            ],
          },
          {
            element: createElement(RequirePermission, { permission: 'content:storyboards:list' }),
            children: [{ path: 'storyboards', lazy: () => import('@/pages/placeholder') }],
          },
          {
            element: createElement(RequirePermission, { permission: 'content:visual_styles:list' }),
            children: [{ path: 'visual-styles', lazy: () => import('@/pages/placeholder') }],
          },

          // ── AI 管理 ──
          {
            element: createElement(RequirePermission, {
              permission: ADMIN_ROUTE_PERMISSIONS.aiConfig,
            }),
            children: [{ path: 'ai-config', lazy: () => import('@/pages/ai-config') }],
          },
          {
            element: createElement(RequirePermission, { permission: 'ai_tasks:list' }),
            children: [{ path: 'ai-tasks', lazy: () => import('@/pages/ai-tasks') }],
          },
          {
            element: createElement(RequirePermission, { permission: 'ai_assistant_runs:list' }),
            children: [
              { path: 'ai-assistant-runs', lazy: () => import('@/pages/ai-assistant-runs') },
              {
                path: 'ai-assistant-runs/skills/:skillId',
                lazy: () => import('@/pages/ai-assistant-runs/skill-consumption'),
              },
              {
                path: 'ai-assistant-runs/screenplays/:screenplayId',
                lazy: () => import('@/pages/ai-assistant-runs/screenplay-consumption'),
              },
            ],
          },
          {
            element: createElement(RequirePermission, { permission: 'ai_assistant_runs:detail' }),
            children: [
              {
                path: 'ai-assistant-runs/:runId',
                lazy: () => import('@/pages/ai-assistant-runs/detail'),
              },
            ],
          },
          {
            element: createElement(RequirePermission, { permission: 'ai_error_rules:list' }),
            children: [{ path: 'ai-error-rules', lazy: () => import('@/pages/ai-error-rules') }],
          },
          {
            element: createElement(RequirePermission, { permission: 'task_queue:monitor:list' }),
            children: [
              { path: 'task-queue-monitor', lazy: () => import('@/pages/task-queue-monitor') },
            ],
          },
          {
            element: createElement(RequirePermission, {
              permission: ADMIN_ROUTE_PERMISSIONS.aiUsage,
            }),
            children: [{ path: 'ai-usage', lazy: () => import('@/pages/ai-usage') }],
          },
          {
            element: createElement(RequirePermission, { permission: 'ai_prompt_commands:list' }),
            children: [
              { path: 'ai-prompt-commands', lazy: () => import('@/pages/ai-prompt-commands') },
            ],
          },
          {
            element: createElement(RequirePermission, { permission: 'ai_skills:list' }),
            children: [{ path: 'ai-skills', lazy: () => import('@/pages/ai-skills') }],
          },

          // ── 模型中心（Provider Hub） ──
          {
            element: createElement(RequirePermission, { permission: 'provider_hub:models:list' }),
            children: [
              { path: 'provider-hub', lazy: () => import('@/pages/provider-hub/overview') },
            ],
          },
          {
            element: createElement(RequirePermission, { permission: 'provider_hub:models:list' }),
            children: [
              { path: 'provider-hub/models', lazy: () => import('@/pages/provider-hub/models') },
            ],
          },
          {
            element: createElement(RequirePermission, {
              permission: 'provider_hub:providers:list',
            }),
            children: [
              {
                path: 'provider-hub/providers',
                lazy: () => import('@/pages/provider-hub/providers'),
              },
            ],
          },
          {
            element: createElement(RequirePermission, { permission: 'provider_hub:accounts:list' }),
            children: [
              {
                path: 'provider-hub/accounts',
                lazy: () => import('@/pages/provider-hub/accounts'),
              },
            ],
          },
          {
            element: createElement(RequirePermission, { permission: 'provider_hub:routes:list' }),
            children: [
              { path: 'provider-hub/routes', lazy: () => import('@/pages/provider-hub/routes') },
            ],
          },
          {
            element: createElement(RequirePermission, { permission: 'provider_hub:monitor:list' }),
            children: [
              { path: 'provider-hub/monitor', lazy: () => import('@/pages/provider-hub/monitor') },
            ],
          },
          {
            element: createElement(RequirePermission, { permission: 'provider_hub:pricing:list' }),
            children: [
              {
                path: 'provider-hub/cost-pricing',
                lazy: () => import('@/pages/provider-hub/cost-pricing'),
              },
            ],
          },
          {
            element: createElement(RequirePermission, { permission: 'provider_hub:monitor:list' }),
            children: [
              {
                path: 'provider-hub/health-events',
                lazy: () => import('@/pages/provider-hub/health-events'),
              },
            ],
          },

          // ── 财务中心 ──
          {
            element: createElement(RequirePermission, {
              permission: ADMIN_ROUTE_PERMISSIONS.credits,
            }),
            children: [{ path: 'credits', lazy: () => import('@/pages/credits') }],
          },
          {
            element: createElement(RequirePermission, {
              permission: ADMIN_ROUTE_PERMISSIONS.rechargeOrders,
            }),
            children: [{ path: 'recharge-orders', lazy: () => import('@/pages/recharge-orders') }],
          },
          {
            element: createElement(RequirePermission, {
              permission: ADMIN_ROUTE_PERMISSIONS.creditPackages,
            }),
            children: [{ path: 'credit-packages', lazy: () => import('@/pages/credit-packages') }],
          },
          {
            element: createElement(RequirePermission, {
              permission: ADMIN_ROUTE_PERMISSIONS.creditPricing,
            }),
            children: [{ path: 'credit-pricing', lazy: () => import('@/pages/credit-pricing') }],
          },

          // ── 统计管理 ──
          {
            element: createElement(RequirePermission, { permission: 'monitoring:errors:list' }),
            children: [
              {
                path: 'frontend-error-issues',
                lazy: () => import('@/pages/frontend-error-issues'),
              },
              {
                path: 'frontend-error-issues/:issueId',
                lazy: () => import('@/pages/frontend-error-issues/detail'),
              },
            ],
          },
          {
            element: createElement(RequirePermission, { permission: 'monitoring:analytics:list' }),
            children: [
              { path: 'analytics-overview', lazy: () => import('@/pages/analytics-overview') },
            ],
          },

          // ── 系统设置 ──
          {
            element: createElement(RequirePermission, {
              permission: ADMIN_ROUTE_PERMISSIONS.admins,
            }),
            children: [{ path: 'admins', lazy: () => import('@/pages/admins') }],
          },
          {
            element: createElement(RequirePermission, {
              permission: ADMIN_ROUTE_PERMISSIONS.roles,
            }),
            children: [{ path: 'roles', lazy: () => import('@/pages/roles') }],
          },
          {
            element: createElement(RequirePermission, {
              permission: ADMIN_ROUTE_PERMISSIONS.permissions,
            }),
            children: [{ path: 'permissions', lazy: () => import('@/pages/permissions') }],
          },
          {
            element: createElement(RequirePermission, { permission: 'audit_logs:list' }),
            children: [{ path: 'audit-logs', lazy: () => import('@/pages/audit-logs') }],
          },
          {
            element: createElement(RequirePermission, {
              permission: ADMIN_ROUTE_PERMISSIONS.systemConfig,
            }),
            children: [{ path: 'system-config', lazy: () => import('@/pages/system-config') }],
          },
        ],
      },
    ],
  },
]);
